import { useTranslation } from "react-i18next";
import { useState, useEffect } from "react";
import ArrowChatIcon from "../../../assets/icons/arrow-chat.svg";
import UserIcon from "../../../assets/icons/user-grey-bg.svg";
import { useNavigate } from "react-router-dom";
import AcceptManagerModal from "./modals/accept/AcceptManagerModal";
import RefuseManagerModal from "./modals/refuse/RefuseManagerModal";
import DeleteManagerModal from "./modals/delete/DeleteManagerModal";
import { managerService, IManagerInvitation, ICurrentManager } from "../../../services/manager.service";
import { message } from "antd";
import { convertToShortDate } from "../../../utls/FunctionsUtil";
import "./index.less";

const InvitationManagerPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRefuseModal, setShowRefuseModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentManager, setCurrentManager] = useState<ICurrentManager | null>(null);
  const [pendingInvitations, setPendingInvitations] = useState<IManagerInvitation[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvitation, setSelectedInvitation] = useState<IManagerInvitation | null>(null);
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const managerData = await managerService.getCurrentManager();
      setCurrentManager(managerData);

      const invitationsData = await managerService.getPendingInvitations();
      setPendingInvitations(invitationsData);
    } catch (error) {
      messageApi.error(typeof error === "string" ? error : t("error-occurred"));
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptClick = (invitation: IManagerInvitation) => {
    setSelectedInvitation(invitation);
    setShowAcceptModal(true);
  };

  const handleRefuseClick = (invitation: IManagerInvitation) => {
    setSelectedInvitation(invitation);
    setShowRefuseModal(true);
  };

  const handleCloseModals = () => {
    setShowAcceptModal(false);
    setShowRefuseModal(false);
    setShowDeleteModal(false);
    setSelectedInvitation(null);
  };

  const handleConfirmAccept = async () => {
    if (!selectedInvitation) return;

    try {
      await managerService.respondToInvitation(selectedInvitation._id, "accepted");
      messageApi.success(t("invitation-accepted-success", "Invitation acceptée avec succès"));
      fetchData();
    } catch (error) {
      messageApi.error(typeof error === "string" ? error : t("error-occurred"));
    } finally {
      handleCloseModals();
    }
  };

  const handleConfirmRefuse = async () => {
    if (!selectedInvitation) return;

    try {
      await managerService.respondToInvitation(selectedInvitation._id, "rejected");
      messageApi.success(t("invitation-rejected-success", "Invitation refusée avec succès"));
      fetchData();
    } catch (error) {
      messageApi.error(typeof error === "string" ? error : t("error-occurred"));
    } finally {
      handleCloseModals();
    }
  };

  const handleDeleteClick = () => {
    if (!currentManager) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!currentManager) return;

    try {
      await managerService.deleteCurrentManager(currentManager._id);
      messageApi.success(t("manager-deleted-success", "Manager supprimé avec succès"));
      fetchData();
    } catch (error) {
      messageApi.error(typeof error === "string" ? error : t("error-occurred"));
    } finally {
      handleCloseModals();
    }
  };

  return (
    <div className="invitation-manager-page">
      {contextHolder}
      <img width={11} src={ArrowChatIcon} alt="Back" onClick={() => navigate(-1)} />
      <h1 className="invitation-manager-title">{t("manager")}</h1>
      <div className="invitation-manager-wrapper">
        <div className="invitation-manager-title">{t("current-manager")}</div>
        {loading ? (
          <div className="invitation-manager-loading">{t("loading")}</div>
        ) : currentManager ? (
          <div className="invitation-manager-content" onClick={handleDeleteClick}>
            <img src={UserIcon} alt="User" />
            <div className="invitation-manager-content-info">
              <div className="invitation-manager-content-info-name">
                {`${currentManager.firstName} ${currentManager.lastName} . @${currentManager.username}`}
              </div>
              <div className="invitation-manager-content-info-date">{t("manager-since", { date: currentManager.since ? convertToShortDate(currentManager.since, "fr-FR") : "" })}</div>
            </div>
          </div>
        ) : (
          <div className="invitation-manager-no-content">{t("no-current-manager")}</div>
        )}

        <div className="invitation-manager-title mt-40">{t("invitation")}</div>
        {loading ? (
          <div className="invitation-manager-loading">{t("loading")}</div>
        ) : pendingInvitations.length > 0 ? (
          pendingInvitations.map((invitation) => (
            <div className="invitation-content" key={invitation._id}>
              <div className="invitation-user-info">
                <img src={UserIcon} alt="User" />
                <div className="invitation-manager-content-info">
                  <div className="invitation-manager-content-info-name">
                    {`${invitation.sender.firstName} ${invitation.sender.lastName} . @${invitation.sender.username}`}
                  </div>
                  <div className="invitation-manager-content-info-date">{t("invitation-received", { date: invitation.createdAt ? convertToShortDate(invitation.createdAt, "fr-FR") : "" })}</div>
                </div>
              </div>
              <div className="invitation-manager-button-container">
                <button className="invitation-manager-accept-button" onClick={() => handleAcceptClick(invitation)}>
                  {t("accept")}
                </button>
                <button className="invitation-manager-refuse-button" onClick={() => handleRefuseClick(invitation)}>
                  {t("refuse")}
                </button>
              </div>
            </div>
          ))
        ) : (
          <div className="invitation-manager-no-content">{t("no-pending-invitations")}</div>
        )}
      </div>

      {showAcceptModal && selectedInvitation && <AcceptManagerModal onClose={handleCloseModals} onAccept={handleConfirmAccept} />}

      {showRefuseModal && selectedInvitation && <RefuseManagerModal onClose={handleCloseModals} onRefuse={handleConfirmRefuse} />}

      {showDeleteModal && currentManager && <DeleteManagerModal onClose={handleCloseModals} onDelete={handleConfirmDelete} />}
    </div>
  );
};

export default InvitationManagerPage;
